import { useEffect, useRef, useState } from 'react';
import { api, type Documento } from '../api';
import { cita, familiaTipo, fechaLarga } from '../formato';
import { Resaltado } from './Resaltado';

interface Props {
  nro: number;
  re: RegExp | null;
  onCerrar: () => void;
}

function textoExpediente(e: Documento['expediente']): string | null {
  if (!e.prefijo && !e.numero) return null;
  let t = [e.prefijo, e.numero].filter(Boolean).join('-');
  if (e.anio) t += `/${e.anio}`;
  if (e.extension) t += ` (${e.extension.toLowerCase()})`;
  return t;
}

/** Las observaciones suelen ser el visto recortado; sólo se muestran si agregan algo. */
function observacionesUtiles(d: Documento): string | null {
  const o = d.observaciones.trim();
  if (!o) return null;
  return d.visto.toLowerCase().startsWith(o.toLowerCase()) ? null : o;
}

export function Ficha({ nro, re, onCerrar }: Props) {
  const dialogo = useRef<HTMLDialogElement>(null);
  const [doc, setDoc] = useState<Documento | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    const d = dialogo.current;
    if (d && !d.open) d.showModal();
    document.body.classList.add('sin-scroll');
    return () => document.body.classList.remove('sin-scroll');
  }, []);

  useEffect(() => {
    const ctl = new AbortController();
    setDoc(null);
    setError(null);
    api
      .documento(nro, ctl.signal)
      .then(setDoc)
      .catch((e: Error) => e.name !== 'AbortError' && setError(e.message));
    return () => ctl.abort();
  }, [nro]);

  useEffect(() => {
    if (doc) document.title = `${cita(doc.tipo, doc.numero, doc.anio)} · Boletín Oficial de Luján`;
  }, [doc]);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* el navegador no permite copiar */
    }
  };

  const exp = doc ? textoExpediente(doc.expediente) : null;
  const obs = doc ? observacionesUtiles(doc) : null;

  return (
    <dialog
      ref={dialogo}
      className="ficha"
      aria-labelledby="ficha-titulo"
      onClose={onCerrar}
      onClick={(e) => e.target === e.currentTarget && dialogo.current?.close()}
    >
      <div className="ficha__panel">
        <div className="ficha__barra">
          <button className="boton boton--cerrar" onClick={() => dialogo.current?.close()}>
            Cerrar
          </button>
        </div>

        {error && (
          <div className="aviso aviso--error" role="alert">
            <p>No se pudo abrir la norma: {error}</p>
          </div>
        )}
        {!doc && !error && <p className="ficha__cargando">Cargando la norma…</p>}

        {doc && (
          <article className={`ficha__contenido norma--${familiaTipo(doc.tipo)}`}>
            <h2 id="ficha-titulo" className="ficha__titulo">
              {cita(doc.tipo, doc.numero, doc.anio)}
            </h2>

            <dl className="ficha__datos">
              <div>
                <dt>Fecha</dt>
                <dd>
                  <time dateTime={doc.fecha ?? undefined}>{fechaLarga(doc.fecha)}</time>
                </dd>
              </div>
              {exp && (
                <div>
                  <dt>Expediente</dt>
                  <dd>{exp}</dd>
                </div>
              )}
            </dl>

            <div className="ficha__acciones">
              <a className="boton boton--primario" href={api.urlPdf(doc.nroInterno)} target="_blank" rel="noopener">
                Abrir PDF
              </a>
              <a className="boton" href={api.urlPdf(doc.nroInterno)} download>
                Descargar PDF
              </a>
              <button className="boton" onClick={copiar}>
                {copiado ? 'Enlace copiado' : 'Copiar enlace'}
              </button>
            </div>

            {doc.visto && (
              <section className="ficha__seccion">
                <h3>Asunto</h3>
                <p>
                  <Resaltado texto={doc.visto} re={re} />
                </p>
              </section>
            )}
            {obs && (
              <section className="ficha__seccion">
                <h3>Observaciones</h3>
                <p>
                  <Resaltado texto={obs} re={re} />
                </p>
              </section>
            )}
            <section className="ficha__seccion">
              <h3>Texto de la norma</h3>
              {doc.texto ? (
                <div className="ficha__texto">
                  <Resaltado texto={doc.texto} re={re} />
                </div>
              ) : (
                <p className="ficha__vacio">El texto no está cargado. Consulte el PDF.</p>
              )}
            </section>
          </article>
        )}
      </div>
    </dialog>
  );
}
